import React from 'react';
import {
  ResourceList,
  ResourceItem,
  Stack,
  Button,
  TextContainer,
  ButtonGroup,
  Icon,
  Tooltip,
  Badge,
  TextStyle
} from '@shopify/polaris';
import {
  EditMajor,
  DeleteMajor,
  DuplicateMinor,
  PlayMinor
} from '@shopify/polaris-icons';

/**
 * A component to display a list of workflows with actions
 * 
 * @param {Object} props
 * @param {Array} props.workflows - List of workflows to display
 * @param {Array} props.selectedTemplates - Currently selected templates
 * @param {Function} props.handleEditWorkflow - Callback when a workflow is edited
 * @param {Function} props.handleDelete - Callback when a workflow is deleted
 * @param {Function} props.handleDuplicate - Callback when a workflow is duplicated
 * @param {Function} props.executeWorkflow - Callback when a workflow is executed
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Array} props.templates - List of all templates
 * @param {Array} props.aiModels - List of all AI models
 */
const WorkflowList = ({
  workflows,
  selectedTemplates,
  handleEditWorkflow,
  handleDelete,
  handleDuplicate,
  executeWorkflow,
  disabled = false,
  templates = [],
  aiModels = []
}) => {
  if (!workflows || workflows.length === 0) {
    return (
      <TextContainer>
        <p>No workflows found. Create a new workflow to get started.</p>
      </TextContainer>
    );
  }
  
  // Helper function to calculate total credit cost for a workflow
  const calculateWorkflowCreditCost = (workflow) => {
    if (!workflow || !workflow.templates || !templates || !aiModels) return 0;
    
    let totalCost = 0;
    
    // For each template in the workflow
    workflow.templates.forEach(templateId => {
      // Find the template
      const template = templates.find(t => t.id === templateId);
      if (!template) return;
      
      // Tool templates cost 0 credits
      if (template.type === 'tool') {
        // Don't add any cost for tool templates
        console.log('Skipping tool template in credit calculation:', template.name);
      } else {
        // Get the output fields count
        const outputFieldCount = (template.outputFields?.length || 0) + 
                               (template.outputMetafields?.length || 0);
        
        // Find the model
        const model = template.modelId ? 
                     aiModels.find(m => m.id === template.modelId) : 
                     aiModels.find(m => m.isDefault);
        
        // Calculate cost
        const costPerOutput = model?.creditCostPerOutput || 1;
        const templateCost = outputFieldCount * costPerOutput;
        
        totalCost += templateCost;
      }
    });
    
    console.log('Workflow credit cost calculation:', {
      workflowId: workflow.id,
      workflowName: workflow.name,
      templateCount: workflow.templates?.length || 0,
      totalCost
    });
    
    return totalCost;
  };

  return (
    <ResourceList
      resourceName={{ singular: 'workflow', plural: 'workflows' }}
      items={workflows}
      renderItem={(item) => (
        <ResourceItem
          id={item.id}
          accessibilityLabel={`Workflow ${item.name}`}
        >
          <Stack distribution="equalSpacing">
            <Stack.Item fill>
              <Stack vertical spacing="tight">
                <TextStyle variation="strong">{item.name}</TextStyle>
                <Stack spacing="tight">
                  <TextStyle variation="subdued">
                    Templates: {item.templates?.length || 0}
                  </TextStyle>
                  <TextStyle variation="subdued">
                    •
                  </TextStyle>
                  <Tooltip content={`Total credit cost for all templates in this workflow`}>
                    <Badge status="info">Total Credits: {calculateWorkflowCreditCost(item)}</Badge>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <ButtonGroup>
                {executeWorkflow && (
                  <Tooltip content="Execute workflow">
                    <Button
                      icon={PlayMinor}
                      primary
                      onClick={() => executeWorkflow(item)}
                      disabled={disabled}
                    />
                  </Tooltip>
                )}
                <Tooltip content="Edit workflow">
                  <Button
                    icon={EditMajor}
                    onClick={() => handleEditWorkflow(item)}
                  />
                </Tooltip>
                <Tooltip content="Duplicate workflow">
                  <Button
                    icon={DuplicateMinor}
                    onClick={() => handleDuplicate(item)}
                  />
                </Tooltip>
                <Tooltip content="Delete workflow">
                  <Button
                    icon={DeleteMajor}
                    destructive
                    onClick={() => handleDelete(item, 'workflow')}
                  />
                </Tooltip>
              </ButtonGroup>
            </Stack.Item>
          </Stack>
        </ResourceItem>
      )}
    />
  );
};

export default WorkflowList;
